/**
 * Shared building blocks for the 5 Phase 2 season-intent cards.
 *
 * Used by SeasonSummaryCard, CupScheduleCard, CupRulesCard,
 * MoveChangeCard, NewMovesCard.  Lives here so any visual refinement
 * (status badge, type colors, date formatting) touches one file
 * instead of five.
 */

import { isSafeUrl } from "../../lib/safeUrl";
import type {
  MoveChange,
  NewMove,
  SeasonOverview,
  CupSlot,
} from "../../types";
import { TYPE_TEXT_COLORS } from "../../utils/typeColors";

// ---------------------------------------------------------------------------
// Color palettes (type text colours come from utils/typeColors)
// ---------------------------------------------------------------------------

// Chip variant — background + border + text for "type pill" rendering
// in CupRulesCard.  Different colors per type for fast visual scan.
export const TYPE_CHIP_CLASSES: Record<string, string> = {
  fire: "bg-orange-500/15 text-orange-300 border-orange-500/25",
  water: "bg-blue-500/15 text-blue-300 border-blue-500/25",
  grass: "bg-green-500/15 text-green-300 border-green-500/25",
  electric: "bg-yellow-500/15 text-yellow-300 border-yellow-500/25",
  psychic: "bg-pink-500/15 text-pink-300 border-pink-500/25",
  ice: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",
  dragon: "bg-indigo-500/15 text-indigo-300 border-indigo-500/25",
  dark: "bg-gray-500/15 text-gray-300 border-gray-500/25",
  fairy: "bg-pink-400/15 text-pink-200 border-pink-400/25",
  normal: "bg-gray-400/15 text-gray-200 border-gray-400/25",
  fighting: "bg-red-500/15 text-red-300 border-red-500/25",
  flying: "bg-sky-500/15 text-sky-300 border-sky-500/25",
  poison: "bg-purple-500/15 text-purple-300 border-purple-500/25",
  ground: "bg-amber-600/15 text-amber-400 border-amber-600/25",
  rock: "bg-amber-500/15 text-amber-300 border-amber-500/25",
  bug: "bg-lime-500/15 text-lime-300 border-lime-500/25",
  ghost: "bg-violet-500/15 text-violet-300 border-violet-500/25",
  steel: "bg-slate-500/15 text-slate-300 border-slate-500/25",
};

export const LEAGUE_BADGE_CLASSES: Record<string, string> = {
  great: "text-blue-300 bg-blue-500/10 border-blue-500/20",
  ultra: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  master: "text-violet-300 bg-violet-500/10 border-violet-500/20",
  multi: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
};

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

export function fmtDate(iso?: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Buff / nerf inference for MoveChange rows
// ---------------------------------------------------------------------------

type ChangeDirection = "buff" | "nerf" | "neutral";

export function changeDirection(c: MoveChange): ChangeDirection {
  const before = c.before == null ? null : Number(c.before);
  const after = c.after == null ? null : Number(c.after);
  if (before == null || after == null || isNaN(before) || isNaN(after)) {
    // Energy-only changes ("energy cost decreased") have no explicit
    // numbers but the kind + notes tell us the direction.
    const notes = (c.notes ?? "").toLowerCase();
    const kind = (c.change_kind ?? "").toLowerCase();
    if (kind.includes("energy")) {
      if (notes.includes("decreased")) return "buff";
      if (notes.includes("increased")) {
        // energyGain increased = buff (fast move); energy cost increased = nerf.
        return notes.includes("generation") || notes.includes("gain") ? "buff" : "nerf";
      }
    }
    return "neutral";
  }
  const kind = (c.change_kind ?? "").toLowerCase();
  if (kind.includes("power")) return after > before ? "buff" : after < before ? "nerf" : "neutral";
  if (kind.includes("energy")) {
    // Charged-move energy cost: less is better.  Fast-move energyGain: more is better.
    // The two fields share the kind label so we infer from the magnitude convention
    // (fast moves have small positive gains; charged moves have large negative costs).
    if (Math.abs(before) > 20 || Math.abs(after) > 20) {
      // Charged: less absolute energy = buff
      return Math.abs(after) < Math.abs(before) ? "buff" : Math.abs(after) > Math.abs(before) ? "nerf" : "neutral";
    }
    // Fast: more energyGain = buff
    return after > before ? "buff" : after < before ? "nerf" : "neutral";
  }
  return "neutral";
}

// ---------------------------------------------------------------------------
// Reusable mini-components
// ---------------------------------------------------------------------------

export function ChangeArrow({ direction }: { direction: ChangeDirection }) {
  if (direction === "buff") return <span className="text-emerald-400 text-xs" aria-label="buff">↑</span>;
  if (direction === "nerf") return <span className="text-red-400 text-xs" aria-label="nerf">↓</span>;
  return <span className="text-white/30 text-xs" aria-label="changed">→</span>;
}

export function MoveChangeRow({ c }: { c: MoveChange }) {
  const dir = changeDirection(c);
  const hasNumbers = c.before != null && c.after != null;
  return (
    <li className="text-xs">
      <div className="flex items-baseline gap-2">
        <ChangeArrow direction={dir} />
        {/* No truncate — long move names should wrap to a second line on
            mobile rather than ellipsis-clip out of sight. */}
        <span className="text-white/85 font-medium flex-1 min-w-0 break-words">{c.move_name ?? "?"}</span>
        {hasNumbers ? (
          <span className="text-white/50 tabular-nums shrink-0">
            {String(c.before)} <span className="text-white/30">→</span> {String(c.after)}
          </span>
        ) : (
          <span className="text-white/40 italic text-right max-w-[55%] break-words">
            {c.notes ?? c.change_kind?.replace(/_/g, " ") ?? "changed"}
          </span>
        )}
      </div>
      {/* Secondary notes line — render only when we already showed numbers
          above (otherwise notes is the primary message in the row).
          Surfaces the extra "energy cost decreased" / "Trainer Battles
          only" context that the LLM extractor packs into notes when a
          single move has multiple change aspects.  Without this, multi-
          aspect changes like Dragon Ascent (power 150→110 + energy cost
          decreased) only showed the power half. */}
      {hasNumbers && c.notes && (
        <p className="text-[10px] text-white/40 mt-0.5 pl-5 italic">{c.notes}</p>
      )}
    </li>
  );
}

export function NewMoveChip({ m }: { m: NewMove }) {
  const typeColor = m.move_type ? TYPE_TEXT_COLORS[m.move_type.toLowerCase()] ?? "text-white/55" : "text-white/55";
  const cat = m.move_category === "fast" ? "fast" : m.move_category === "charged" ? "charged" : null;
  return (
    <span>
      <span className={typeColor}>{m.move_name}</span>
      {cat && <span className="text-white/30"> ({cat})</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Cup grouping — multiple cups can run concurrently in the same week
// ---------------------------------------------------------------------------

interface CupWeek {
  week_start?: string;
  week_end?: string | null;
  cups: CupSlot[];
}

/**
 * Group flat cup_schedule entries by (week_start, week_end) so multi-cup
 * weeks render under one date header.  Preserves source order within
 * each week.  Handles the case where the LLM extractor emits multiple
 * concurrent cups for one week (e.g. June 16-23 has both
 * "Master League: Mega Edition" AND "Sunshine Cup: Great League Edition").
 */
export function groupCupsByWeek(cups: CupSlot[]): CupWeek[] {
  const weeks: CupWeek[] = [];
  const keyToWeek = new Map<string, CupWeek>();
  for (const c of cups) {
    const key = `${c.week_start ?? ""}|${c.week_end ?? ""}`;
    let week = keyToWeek.get(key);
    if (!week) {
      week = { week_start: c.week_start, week_end: c.week_end, cups: [] };
      keyToWeek.set(key, week);
      weeks.push(week);
    }
    week.cups.push(c);
  }
  return weeks;
}

/**
 * Render one week (date range header + nested cup rows).  Used by the
 * cup-list rendering in SeasonSummaryCard and CupScheduleCard so multi-
 * cup weeks read as "this week has these N cups running" rather than
 * three identical-date rows the user has to mentally group.
 */
export function CupWeekBlock({
  week,
  badge,
}: {
  week: CupWeek;
  badge?: { label: string; cls: string } | null;
}) {
  const dateLabel = (
    <span className="text-white/55 tabular-nums">
      {fmtDate(week.week_start)}{week.week_end && <>–{fmtDate(week.week_end)}</>}
    </span>
  );
  return (
    <li className="text-xs">
      <div className="flex items-center gap-2 mb-0.5">
        {dateLabel}
        {badge && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls} shrink-0`}>
            {badge.label}
          </span>
        )}
        {week.cups.length > 1 && (
          <span className="text-[10px] text-white/35">· {week.cups.length} cups</span>
        )}
      </div>
      <ul className="pl-3 space-y-0.5">
        {week.cups.map((c, i) => {
          const leagueColor = c.league ? LEAGUE_BADGE_CLASSES[c.league.toLowerCase()] : null;
          return (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-white/30 shrink-0" aria-hidden="true">·</span>
              {/* No truncate — let the cup name wrap to multiple lines on
                  narrow viewports.  Long official cup names like "Pokémon
                  North America International Championships 2026 Cup: Great
                  League Edition" must be fully readable. */}
              <span className="text-white/80 flex-1 min-w-0 break-words">{c.cup_name ?? "(cup)"}</span>
              {leagueColor && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${leagueColor} shrink-0`}>
                  {c.league}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}

// ---------------------------------------------------------------------------
// SeasonHeaderStrip — the consistent header rendered on all 5 season cards
// ---------------------------------------------------------------------------

export function SeasonHeaderStrip({ overview }: { overview: SeasonOverview }) {
  const startsAt = overview.starts_at ?? undefined;
  const endsAt = overview.ends_at ?? undefined;
  const isUpcoming = overview.is_upcoming;
  const isActive = overview.is_active;
  const cupCount = overview.cup_count ?? 0;
  const moveChangeCount = overview.move_change_count ?? 0;
  const newMoveCount = overview.new_move_count ?? 0;
  const pokemonWithNewMoves = overview.pokemon_with_new_moves ?? 0;

  const stateBadge =
    isActive ? { label: "Live", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" }
    : isUpcoming ? { label: "Upcoming", cls: "bg-blue-500/15 text-blue-300 border-blue-500/25" }
    : { label: "Announced", cls: "bg-white/10 text-white/60 border-white/15" };

  // pokemon_with_new_moves is only emitted by season_summary_card; the
  // other 4 cards leave it 0.  Compose the counts line conditionally.
  const countsParts: string[] = [];
  if (cupCount) countsParts.push(`${cupCount} cup${cupCount === 1 ? "" : "s"}`);
  if (moveChangeCount) countsParts.push(`${moveChangeCount} move change${moveChangeCount === 1 ? "" : "s"}`);
  if (newMoveCount) {
    countsParts.push(
      pokemonWithNewMoves
        ? `${newMoveCount} new moves across ${pokemonWithNewMoves} Pokémon`
        : `${newMoveCount} new move${newMoveCount === 1 ? "" : "s"}`
    );
  }
  const countsLine = countsParts.join(" · ");

  return (
    <div className="flex items-start justify-between mb-3 gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white/70 leading-snug">
          {fmtDate(startsAt)}{endsAt && <> – {fmtDate(endsAt)}</>}
        </p>
        {countsLine && (
          <p className="text-[11px] text-white/40 mt-0.5">{countsLine}</p>
        )}
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${stateBadge.cls} shrink-0`}>
        {stateBadge.label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible "Show all / Show less" wrapper
// ---------------------------------------------------------------------------

interface CollapsibleListProps<T> {
  items: T[];
  initial?: number;
  renderItem: (item: T, idx: number) => React.ReactNode;
  itemNoun?: string;
  expanded: boolean;
  onToggle: () => void;
}

export function CollapsibleList<T>({
  items,
  initial = 5,
  renderItem,
  itemNoun = "more",
  expanded,
  onToggle,
}: CollapsibleListProps<T>) {
  const visible = expanded ? items : items.slice(0, initial);
  const hidden = items.length - visible.length;
  return (
    <>
      <ul className="space-y-1">{visible.map((item, i) => renderItem(item, i))}</ul>
      {items.length > initial && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-2 text-[11px] text-indigo-400/70 hover:text-indigo-300 transition-colors"
          aria-expanded={expanded}
        >
          {expanded
            ? "Show less"
            : `Show all ${items.length} (${hidden} ${itemNoun})`}
        </button>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Source link footer (re-used by every season card)
// ---------------------------------------------------------------------------

export function SeasonSourceLink({ sourceUrl }: { sourceUrl?: string }) {
  if (!isSafeUrl(sourceUrl)) return null;
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06]">
      <a
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] text-indigo-400/70 hover:text-indigo-300 transition-colors"
      >
        Read the announcement on pokemongo.com →
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Light parser: extract structured fields from rules_summary narrative
// (Haiku emits these as one free-text string per cup).  Best-effort
// regex — if it fails we just don't surface the structured chips and
// the narrative gets rendered as-is below.
// ---------------------------------------------------------------------------

interface ParsedCupRules {
  cp_cap: number | null;
  eligible_types: string[];
  banned_pokemon: string[];
  raw: string;
}

// "Only Fairy-, Normal-, Psychic-, and Water-type Pokémon are eligible"
// Has to handle both:
//   "Only X, Y, Z type Pokémon are eligible"
//   "Only X-, Y-, Z-type Pokémon are eligible"
//   "Only X and Y types are allowed"
// Strategy: capture everything between "only" and either "type"/"types" or
// the word "are eligible" itself, then split on commas / "and" / hyphens.
const _TYPES_RE = /only\s+([\w\s,\-/]+?)\s+(?:type[s]?\s+(?:Pok[eé]mon\s+)?(?:are\s+)?(?:eligible|allowed)|are\s+eligible)/i;
// "Banned: Charizard, ..."
const _BANNED_RE = /(?:banned|not\s+eligible|excluded|ineligible)[:\s]+([A-Z][\w\s\-(),'.]*?)(?:\s*(?:\.|$))/i;

const _KNOWN_TYPES = new Set([
  "fire", "water", "grass", "electric", "psychic", "ice", "dragon", "dark",
  "fairy", "normal", "fighting", "flying", "poison", "ground", "rock", "bug",
  "ghost", "steel",
]);

export function parseCupRules(summary?: string | null): ParsedCupRules {
  if (!summary) return { cp_cap: null, eligible_types: [], banned_pokemon: [], raw: "" };
  // CP cap — search anywhere
  const cpMatch = summary.match(/(\d{3,5})\s*cp/i);
  const cpCap = cpMatch ? Number(cpMatch[1]) : null;

  // Eligible types
  const typesMatch = summary.match(_TYPES_RE);
  const eligibleTypes: string[] = [];
  if (typesMatch) {
    const raw = typesMatch[1];
    raw.split(/[,/]|\sand\s/).forEach((tok) => {
      const norm = tok.trim().toLowerCase().replace(/-$/, "").replace(/-type$/, "");
      if (_KNOWN_TYPES.has(norm)) eligibleTypes.push(norm);
    });
  }

  // Banned list
  const bannedMatch = summary.match(_BANNED_RE);
  const banned: string[] = [];
  if (bannedMatch) {
    bannedMatch[1]
      .split(/,|\sand\s/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((name) => banned.push(name));
  }

  return {
    cp_cap: cpCap,
    eligible_types: eligibleTypes,
    banned_pokemon: banned,
    raw: summary,
  };
}
